/**
 * Read-side normalizer for transaction-rule conditions — FE-RULES-1.
 *
 * The backend persists and accepts the polymorphic wire union
 * `IRuleCondition[] | IConditionGroup` (see
 * backend/src/validators/transactionRuleValidator.ts and the `evaluateCondition`
 * branch in backend/src/handlers/transactionRuleHandler.ts). The frontend app
 * layer (Redux, components, BatchApplyRuleDialog, Rules page) consumes a flat
 * `RuleCondition[]` plus a `hasNestedConditions: boolean` provenance flag on
 * the normalized rule.
 *
 * This module performs a recursive, non-mutating conversion between the two
 * contracts at the transport/state boundary. It does NOT change the backend
 * contract and does NOT flatten grouped rules into an editable payload —
 * grouped rules stay marked `hasNestedConditions: true` so the UI can gate
 * the edit action until a lossless nested editor exists.
 *
 * Conversions applied to each leaf:
 *   - `value` is coerced via `String(value)` so the app-side `value: string`
 *     invariant holds for every operator except `between`.
 *   - `between` with a numeric `value2` is encoded as `"min,max"` to match
 *     the backend evaluator's `targetValue.split(',').map(Number)` parser.
 *   - When `operator === 'between'` and `value2` is absent, the wire already
 *     carries an encoded string (e.g. seed data) and is passed through as-is
 *     via `String(value)`.
 *
 * Deterministic leaf order: parent's `rules` are appended in array order, then
 * each child group is recursed depth-first (root-then-child). Valid empty
 * `groups: []` contributes nothing but still flips the provenance flag because
 * the root input was a non-array group object — `evaluateConditionGroup` on
 * the backend would have used the root `operator` to combine its rules, and
 * that boolean-group operator is lost when flattened.
 *
 * Provenance rule (Oracle adjudication): any non-array input is treated as
 * grouped provenance — `hasNestedConditions` is `true` whenever the wire
 * payload is a `RuleConditionGroupWire`, even when `groups` is missing or
 * empty and the root has no children. Flat arrays stay `false`.
 *
 * Wire-side types live next door in `./types` and are re-exported here so
 * consumers can import the entire contract surface from a single module path.
 * The relative import is required because this file is consumed by both the
 * frontend Vite build (via path alias) and the Testify Jest runner (which has
 * no `@/` mapping for `frontend/src`).
 */
import type {
  RuleCondition,
  RuleConditionGroupWire,
  RuleConditionWire,
  RuleConditionsWire,
  RuleOperator,
  RuleWireOperator,
  TransactionRule,
  TransactionRuleWire,
} from './types';

// Re-export the wire-side types so callers can import everything from one
// place. App-side `RuleCondition` and `TransactionRule` are also re-exported
// for the same reason.
export type {
  RuleCondition,
  RuleConditionGroupWire,
  RuleConditionWire,
  RuleConditionsWire,
  RuleOperator,
  RuleWireOperator,
  TransactionRule,
  TransactionRuleWire,
};

/** App rule augmented with nested-condition provenance. */
export type NormalizedTransactionRule = TransactionRule & {
  hasNestedConditions: boolean;
};

/**
 * Internal recursive worker. Walks the wire union and returns the flat leaves
 * plus the provenance flag. Pure / non-mutating — every collection traversed
 * is read-only and every returned object is a fresh allocation.
 */
function flattenInternal(
  conditions: RuleConditionsWire,
): { conditions: RuleCondition[]; hasNestedConditions: boolean } {
  if (Array.isArray(conditions)) {
    return {
      conditions: conditions.map(toAppCondition),
      hasNestedConditions: false,
    };
  }
  // Any non-array input is a grouped wire root → provenance is always true,
  // even for an empty `groups: []`. The backend would have dispatched this
  // through `evaluateConditionGroup` using the root `operator`, and that
  // boolean-group semantics is what `hasNestedConditions` records.
  return walkGroup(conditions);
}

function walkGroup(
  group: RuleConditionGroupWire,
): { conditions: RuleCondition[]; hasNestedConditions: boolean } {
  const flat: RuleCondition[] = [];

  // Root rules first — preserves array order in the leaf stream.
  for (const rule of group.rules) {
    flat.push(toAppCondition(rule));
  }

  // Recurse defensively into deeper groups; the current backend validator
  // accepts one level but the wire shape allows arbitrary depth.
  for (const child of group.groups ?? []) {
    flat.push(...walkGroup(child).conditions);
  }

  return { conditions: flat, hasNestedConditions: true };
}

function toAppCondition(wire: RuleConditionWire): RuleCondition {
  if (wire.operator === 'between' && wire.value2 !== undefined) {
    return {
      field: wire.field,
      operator: wire.operator,
      value: `${wire.value},${wire.value2}`,
    };
  }
  return {
    field: wire.field,
    operator: wire.operator,
    value: String(wire.value),
  };
}

/**
 * Public flat normalizer. Discards the provenance flag — callers that need
 * it (Redux, /rules route, BatchApplyRuleDialog) must use
 * {@link normalizeTransactionRule} instead.
 *
 * Flat arrays (including the backend-rejected empty form) yield `[]`. A
 * non-array input always flattens and the caller can read the provenance flag
 * via {@link normalizeTransactionRule}.
 */
export function normalizeRuleConditions(
  conditions: RuleConditionsWire,
): RuleCondition[] {
  return flattenInternal(conditions).conditions;
}

/**
 * Normalize a single rule's wire conditions and stamp nested-condition
 * provenance onto the result. Pass-through copy of every other field; the
 * input object is never mutated.
 */
export function normalizeTransactionRule(
  rule: TransactionRuleWire,
): NormalizedTransactionRule {
  const { conditions, hasNestedConditions } = flattenInternal(rule.conditions);
  return {
    ...rule,
    conditions,
    hasNestedConditions,
  };
}

/**
 * Normalize a list of rules, preserving order. Each output element carries its
 * own provenance flag; callers that want a single batch-level flag must reduce
 * over the result themselves.
 */
export function normalizeTransactionRules(
  rules: TransactionRuleWire[],
): NormalizedTransactionRule[] {
  return rules.map(normalizeTransactionRule);
}
